import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
	Loader2,
	AlertTriangle,
	ChevronDown,
	SlidersHorizontal,
	Upload,
	Trash2,
	Zap,
	CalendarClock,
	Check,
	Plus,
	ArrowLeft,
	ArrowRight,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { MultiSelect } from "../ui/multi-select";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { cn } from "../../lib/utils";
import RecorrenciaFields from "../RecorrenciaFields";
import AcoesDaCampanhaFields from "../AcoesDaCampanhaFields";
import TemplateMessage from "../TemplateMessage";

// Quantas variáveis o corpo do modelo pede: {{1}}, {{2}}…
const contarVariaveis = (componentes = []) => {
	const corpo = (componentes || []).find(
		(c) => String(c?.type).toUpperCase() === "BODY",
	);

	const achadas = String(corpo?.text || "").match(/\{\{\s*\d+\s*\}\}/g) || [];

	return new Set(achadas.map((v) => v.replace(/\D/g, ""))).size;
};

// A prévia do que o cliente vai receber, no mesmo desenho da conversa.
const previaDoModelo = (modelo, valores) => {
	const componentes = modelo?.components || [];

	const acharTipo = (tipo) =>
		componentes.find((c) => String(c?.type).toUpperCase() === tipo);

	const preencher = (texto) =>
		String(texto || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (todo, indice) => {
			const valor = valores[Number(indice) - 1];

			return valor ? valor : todo;
		});

	const cabecalho = acharTipo("HEADER");
	const botoes = acharTipo("BUTTONS");

	return {
		tipo: "whatsapp",
		nome: modelo?.nome,
		categoria: modelo?.categoria,
		cabecalho: cabecalho
			? {
					tipo: String(cabecalho.format || "TEXT").toLowerCase(),
					texto: preencher(cabecalho.text),
				}
			: null,
		corpo: preencher(acharTipo("BODY")?.text),
		rodape: acharTipo("FOOTER")?.text || null,
		botoes: (botoes?.buttons || []).map((b) => ({
			tipo: String(b.type || "").toLowerCase(),
			texto: b.text,
			url: b.url,
			telefone: b.phone_number,
		})),
	};
};

// Quantas ações estão ligadas — é o que o resumo da seção mostra fechada.
const contarAcoes = (acoes) =>
	Object.values(acoes || {}).filter((valor) =>
		Array.isArray(valor) ? valor.length > 0 : Boolean(valor),
	).length;

// Os três passos, na ordem em que a pessoa pensa: para quem, o quê, quando.
const PASSOS = [
	{
		numero: 1,
		titulo: "Para quem",
		pergunta: "Quem vai receber essa mensagem?",
		explicacao:
			"Escolha as pessoas e por qual dos seus WhatsApps a mensagem sai. É nesse número que elas vão responder.",
	},
	{
		numero: 2,
		titulo: "O que dizer",
		pergunta: "O que essas pessoas vão receber?",
		explicacao:
			"Escreva a mensagem uma vez. Todo mundo recebe a mesma coisa, com o nome de cada um no lugar de {{name}}.",
	},
	{
		numero: 3,
		titulo: "Quando enviar",
		pergunta: "Quando isso deve sair?",
		explicacao:
			"Sem data, a campanha fica guardada e só sai quando você mandar. Com data, ela dispara sozinha na hora.",
	},
];

// Um desenho por passo — três pessoas, um balão de conversa, um relógio.
// Vale mais que um parágrafo para quem nunca fez uma campanha: mostra a
// cena em vez de descrevê-la.
const DesenhoDoPasso = ({ passo }) => (
	<svg
		viewBox="0 0 64 48"
		className="h-14 w-20 shrink-0"
		aria-hidden="true"
		fill="none"
	>
		{passo === 1 && (
			<g>
				<circle cx="12" cy="18" r="6" className="fill-primary/25" />
				<path d="M4 34c0-4.4 3.6-8 8-8s8 3.6 8 8" className="fill-primary/25" />
				<circle cx="32" cy="16" r="7" className="fill-primary/60" />
				<path d="M23 34c0-5 4-9 9-9s9 4 9 9" className="fill-primary/60" />
				<circle cx="52" cy="18" r="6" className="fill-primary/25" />
				<path d="M44 34c0-4.4 3.6-8 8-8s8 3.6 8 8" className="fill-primary/25" />
			</g>
		)}
		{passo === 2 && (
			<g>
				<rect
					x="8"
					y="8"
					width="48"
					height="26"
					rx="6"
					className="fill-primary/60"
				/>
				<path d="M18 34l-2 8 10-8z" className="fill-primary/60" />
				<rect x="15" y="15" width="28" height="3" rx="1.5" className="fill-card" />
				<rect x="15" y="22" width="20" height="3" rx="1.5" className="fill-card" />
			</g>
		)}
		{passo === 3 && (
			<g>
				<circle cx="32" cy="24" r="15" className="fill-primary/25" />
				<circle cx="32" cy="24" r="11" className="fill-primary/60" />
				<path
					d="M32 17v7l5 3"
					className="stroke-card"
					strokeWidth="2.5"
					strokeLinecap="round"
				/>
			</g>
		)}
	</svg>
);

const CampaignSchema = Yup.object().shape({
	name: Yup.string().min(2, "Muito curto!").required("Obrigatório"),
	// O corpo deixa de ser obrigatório quando a campanha sai por modelo: o
	// texto que vai ao cliente é o do modelo aprovado, não este.
	body: Yup.string().when("metaTemplateChave", {
		is: (chave) => !chave,
		then: (schema) => schema.required("Obrigatório"),
		otherwise: (schema) => schema,
	}),
	whatsappId: Yup.string().required("Obrigatório"),
	intervalSeconds: Yup.number().min(1, "Mínimo 1").required("Obrigatório"),
});

const CampaignModal = ({ open, onClose, campaignId }) => {
	const initialState = {
		name: "",
		body: "",
		whatsappId: "",
		mediaUrl: "",
		intervalSeconds: 20,
		scheduledFor: "",
		phraseListId: "",
		metaTemplateChave: "",
		templateVariables: [],
		recurrence: null,
		sendIfMissed: false,
		intervalSecondsMax: "",
		// O que fazer com o contato depois que a mensagem sai, e o que fazer
		// quando ele responde.
		postActions: {},
		replyActions: {},
		// Textos extras e o que fazer com eles: sortear um por pessoa ou
		// mandar todos, um depois do outro.
		bodyVariations: [],
		messageMode: "random",
	};

	const [listasDeFrases, setListasDeFrases] = useState([]);

	const [campaign, setCampaign] = useState(initialState);
	const { whatsApps } = useWhatsApps();

	const [targetTab, setTargetTab] = useState("contacts");
	const [contactOptions, setContactOptions] = useState([]);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [contactSearch, setContactSearch] = useState("");
	const [tags, setTags] = useState([]);
	const [queues, setQueues] = useState([]);
	// Respostas prontas para reaproveitar aqui: escrever a mesma promoção do
	// zero a cada campanha é o que elas existem para evitar.
	const [modelos, setModelos] = useState([]);
	// Modelos aprovados do WhatsApp oficial — da Cloud API da Meta e do
	// WhatsApp conectado na Zernio, na mesma lista.
	const [modelosWpp, setModelosWpp] = useState([]);
	const [carregandoModelos, setCarregandoModelos] = useState(true);
	const [listas, setListas] = useState([]);
	// Listas e etiquetas em lista: quem separa cliente por etiqueta quase
	// sempre quer falar com dois grupos de uma vez.
	const [selectedListIds, setSelectedListIds] = useState([]);
	const [selectedTagIds, setSelectedTagIds] = useState([]);
	// Grupos são contatos com isGroup — a campanha para grupo manda dentro do
	// grupo, e a extensão deixa marcar um ou vários de uma vez.
	const [grupos, setGrupos] = useState([]);
	const [selectedGroupIds, setSelectedGroupIds] = useState([]);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	// Ritmo, mídia e a anotação de data quase nunca mudam. Ficam guardados
	// atrás de um resumo para o formulário caber numa olhada.
	const [avancadoAberto, setAvancadoAberto] = useState(false);
	const [acoesAberto, setAcoesAberto] = useState(false);
	// "Uma vez" ou "Repetido", como na extensão: é o seletor do topo que
	// decide se os campos de repetição aparecem.
	const [tipoDeEnvio, setTipoDeEnvio] = useState("once");
	// Em qual dos três passos a pessoa está.
	const [passo, setPasso] = useState(1);
	// Raias de todos os quadros, achatadas: o cartão nasce numa raia, e obrigar
	// a escolher quadro e depois raia seriam dois campos para o mesmo destino.
	const [raias, setRaias] = useState([]);
	// Quem pode receber o atendimento na mão quando a campanha dispara.
	const [atendentes, setAtendentes] = useState([]);
	const [anexos, setAnexos] = useState([]);
	// De qual campanha vieram os valores sugeridos. Null = ninguém sugeriu
	// nada, e os campos estão vazios como sempre estiveram.
	const [variaveisAnteriores, setVariaveisAnteriores] = useState(null);
	const [enviandoAnexo, setEnviandoAnexo] = useState(false);
	const anexoRef = useRef(null);

	// Sobe o arquivo para a biblioteca de mídias e guarda só o nome — o envio
	// lê do disco, sem o servidor buscar um endereço dele mesmo.
	const enviarAnexo = async (arquivo) => {
		if (!arquivo) return;

		setEnviandoAnexo(true);
		try {
			const corpo = new FormData();
			corpo.append("medias", arquivo);
			corpo.append("name", arquivo.name);

			const { data } = await api.post("/media-library", corpo, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			setAnexos((atuais) => [
				...atuais,
				{ mediaName: data.mediaUrl, nomeOriginal: arquivo.name },
			]);
		} catch (err) {
			toastError(err);
		}
		setEnviandoAnexo(false);
	};

	useEffect(() => {
		if (!open) return;
		(async () => {
			try {
				const { data } = await api.get("/tags");
				setTags(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
			try {
				const { data } = await api.get("/contacts/", {
					params: { tipo: "grupos", pageNumber: 1 },
				});
				setGrupos(data?.contacts || []);
			} catch (err) {
				// Sem grupos a aba fica vazia; as outras continuam.
			}
			try {
				const { data } = await api.get("/campaign-phrases");
				setListasDeFrases(Array.isArray(data) ? data : []);
			} catch (err) {
				// Sem listas de frases a campanha manda o texto fixo, como sempre.
			}
			try {
				const { data } = await api.get("/queue");
				setQueues(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
			try {
				const { data } = await api.get("/users");
				setAtendentes(data?.users || []);
			} catch (err) {
				// Sem a lista, a ação de entregar o atendimento some; o resto do
				// formulário continua igual.
			}
			try {
				const { data } = await api.get("/kanban");
				setRaias(
					(Array.isArray(data) ? data : []).map((coluna) => ({
						id: coluna.id,
						label: coluna.name,
					})),
				);
			} catch (err) {
				// Sem quadro, a ação de abrir cartão não aparece.
			}
			try {
				const { data } = await api.get("/contact-lists");
				setListas(data?.lists || (Array.isArray(data) ? data : []));
			} catch (err) {
				// Idem: sem listas, a aba fica vazia e as outras continuam.
			}
			try {
				// Lê das respostas rápidas: era uma tela separada de "modelos de
				// mensagem" que guardava a mesma coisa, e quem escrevia um texto
				// novo tinha de adivinhar em qual das duas salvar.
				const { data } = await api.get("/quickAnswers", {
					params: { pageNumber: 1 },
				});
				setModelos(data?.quickAnswers || []);
			} catch (err) {
				// Sem respostas salvas o campo some; escrever a mensagem na mão
				// continua funcionando.
			}
			try {
				const { data } = await api.get("/meta/templates/available");
				setModelosWpp(Array.isArray(data) ? data : []);
			} catch (err) {
				// Sem WhatsApp oficial não há modelo — a campanha segue por texto
				// livre, que é o caminho da conexão por celular.
				setModelosWpp([]);
			}
			setCarregandoModelos(false);
		})();
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("/contacts/", {
						params: { searchParam: contactSearch },
					});
					setContactOptions(data.contacts || []);
				} catch (err) {
					toastError(err);
				}
			};
			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [contactSearch, open]);

	useEffect(() => {
		(async () => {
			if (!campaignId) return;
			try {
				const { data } = await api.get(`/campaigns/${campaignId}`);
				setCampaign((prevState) => ({
					...prevState,
					name: data.name || "",
					body: data.body || "",
					whatsappId: data.whatsappId ? String(data.whatsappId) : "",
					mediaUrl: data.mediaUrl || "",
					intervalSeconds: data.intervalSeconds || 20,
					scheduledFor: data.scheduledFor
						? new Date(data.scheduledFor).toISOString().slice(0, 16)
						: "",
					phraseListId: data.phraseListId ? String(data.phraseListId) : "",
					metaTemplateChave: data.metaTemplateId
						? `meta:${data.metaTemplateId}`
						: "",
					templateVariables: data.templateVariables || [],
					recurrence: data.recurrence || null,
					sendIfMissed: Boolean(data.sendIfMissed),
					intervalSecondsMax: data.intervalSecondsMax || "",
					postActions: data.postActions || {},
					replyActions: data.replyActions || {},
					bodyVariations: data.bodyVariations || [],
					messageMode: data.messageMode || "random",
				}));
				setTipoDeEnvio(data.recurrence ? "recurring" : "once");
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			setCampaign(initialState);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaignId, open]);

	const handleClose = () => {
		onClose();
		setCampaign(initialState);
		setSelectedContacts([]);
		setContactSearch("");
		setSelectedListIds([]);
		setSelectedTagIds([]);
		setSelectedGroupIds([]);
		setSelectedQueueIds([]);
		setTargetTab("contacts");
		setAvancadoAberto(false);
		setAcoesAberto(false);
		setTipoDeEnvio("once");
		setPasso(1);
	};

	// Quantas pessoas essa campanha vai atingir, em português.
	const listasEscolhidas = listas.filter((l) =>
		selectedListIds.includes(String(l.id))
	);
	const tagsEscolhidas = tags.filter((t) =>
		selectedTagIds.includes(String(t.id))
	);
	// "as listas A e B" em vez de "A, B" — a frase é lida em voz alta na
	// cabeça de quem confere antes de criar.
	const enumerar = (nomes) =>
		nomes.length <= 1
			? nomes[0] || ""
			: `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`;
	const resumoDestinatarios = (() => {
		if (targetTab === "contacts") {
			return selectedContacts.length
				? `${selectedContacts.length} contato(s) escolhido(s) na mão`
				: "nenhum contato escolhido ainda";
		}
		if (targetTab === "list") {
			if (!listasEscolhidas.length) return "nenhuma lista escolhida ainda";
			const nomes = listasEscolhidas.map((l) => `“${l.name}”`);
			const qtd = listasEscolhidas.reduce(
				(soma, l) => soma + (l.items?.length || 0),
				0
			);
			const rotulo = listasEscolhidas.length > 1 ? "das listas" : "da lista";
			return qtd
				? `os ${qtd} contatos ${rotulo} ${enumerar(nomes)}`
				: `todos os contatos ${rotulo} ${enumerar(nomes)}`;
		}
		if (targetTab === "tag") {
			if (!tagsEscolhidas.length) return "nenhuma tag escolhida ainda";
			const nomes = tagsEscolhidas.map((t) => `“${t.name}”`);
			return tagsEscolhidas.length > 1
				? `todos os contatos marcados com as tags ${enumerar(nomes)}`
				: `todos os contatos marcados com a tag ${enumerar(nomes)}`;
		}
		if (targetTab === "group") {
			const escolhidos = grupos.filter((g) =>
				selectedGroupIds.includes(String(g.id))
			);
			if (!escolhidos.length) return "nenhum grupo escolhido ainda";
			const nomes = escolhidos.map((g) => `“${g.name || g.number}”`);
			return escolhidos.length > 1
				? `os grupos ${enumerar(nomes)} — todo mundo dentro deles vê a mensagem`
				: `o grupo ${enumerar(nomes)} — todo mundo dentro dele vê a mensagem`;
		}
		const filasEscolhidas = queues.filter((q) =>
			selectedQueueIds.includes(String(q.id))
		);
		if (!filasEscolhidas.length) return "nenhuma fila escolhida ainda";
		const nomes = filasEscolhidas.map((f) => `“${f.name}”`);
		return filasEscolhidas.length > 1
			? `todos os contatos que já foram atendidos nas filas ${enumerar(nomes)}`
			: `todos os contatos que já foram atendidos na fila ${enumerar(nomes)}`;
	})();

	const mergedContactOptions = (() => {
		const map = new Map();
		selectedContacts.forEach((c) => map.set(String(c.id), c));
		contactOptions.forEach((c) => map.set(String(c.id), c));
		return Array.from(map.values()).map((c) => ({
			value: String(c.id),
			label: c.name,
		}));
	})();

	const handleContactsChange = (values) => {
		const lookup = new Map();
		selectedContacts.forEach((c) => lookup.set(String(c.id), c));
		contactOptions.forEach((c) => lookup.set(String(c.id), c));
		setSelectedContacts(
			values.map((v) => lookup.get(v)).filter((c) => Boolean(c))
		);
	};

	const handleSaveCampaign = async (values) => {
		const payload = {
			name: values.name,
			body: values.body,
			whatsappId: +values.whatsappId,
			mediaUrl: values.mediaUrl || null,
			intervalSeconds: +values.intervalSeconds || 20,
			scheduledFor: values.scheduledFor || null,
			phraseListId: values.phraseListId ? +values.phraseListId : null,
			// A chave carrega a origem. Os dois caminhos são guardados em campos
			// diferentes porque são coisas diferentes: o da Meta é chave
			// estrangeira para MetaTemplates; o da Zernio é nome + idioma dentro
			// da conta, que lá é como o modelo é endereçado.
			metaTemplateId: values.metaTemplateChave?.startsWith("meta:")
				? +values.metaTemplateChave.split(":")[1]
				: null,
			zernioTemplate: (() => {
				if (!values.metaTemplateChave?.startsWith("zernio:")) return null;

				const modelo = modelosWpp.find(
					(m) => m.chave === values.metaTemplateChave,
				);

				if (!modelo) return null;

				return {
					accountId: modelo.accountId,
					name: modelo.nome,
					language: modelo.idioma || "pt_BR",
				};
			})(),
			templateVariables: values.metaTemplateChave
				? values.templateVariables || []
				: null,
			recurrence: values.recurrence,
			sendIfMissed: Boolean(values.sendIfMissed),
			intervalSecondsMax: values.intervalSecondsMax
				? +values.intervalSecondsMax
				: null,
			postActions: values.postActions || null,
			replyActions: values.replyActions || null,
			bodyVariations: (values.bodyVariations || []).filter((t) => t.trim()),
			messageMode: values.messageMode === "all" ? "all" : "random",
			attachments: anexos,
		};

		try {
			if (campaignId) {
				await api.put(`/campaigns/${campaignId}`, payload);
			} else {
				if (targetTab === "contacts" && selectedContacts.length > 0) {
					payload.contactIds = selectedContacts.map((c) => +c.id);
				}
				if (targetTab === "list" && selectedListIds.length) {
					payload.contactListIds = selectedListIds.map((id) => +id);
				}
				if (targetTab === "tag" && selectedTagIds.length) {
					payload.tagIds = selectedTagIds.map((id) => +id);
				}
				// Grupo é um contato com isGroup: vai pelo mesmo caminho dos
				// contatos escolhidos na mão, sem rota nova no backend.
				if (targetTab === "group" && selectedGroupIds.length) {
					payload.contactIds = selectedGroupIds.map((id) => +id);
				}
				if (targetTab === "queue" && selectedQueueIds.length) {
					payload.queueIds = selectedQueueIds.map((id) => +id);
				}
				await api.post("/campaigns", payload);
			}
			toast.success("Campanha salva com sucesso.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="tema-campanha max-h-[90vh] overflow-y-auto border-border bg-card text-card-foreground sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>
						{campaignId
							? "Editar campanha"
							: "Preparar um envio para vários contatos"}
					</DialogTitle>
					<p className="text-xs leading-relaxed text-muted-foreground">
						Em três passos. Nada é enviado enquanto você monta.
					</p>
				</DialogHeader>
				<Formik
					initialValues={campaign}
					enableReinitialize={true}
					validationSchema={CampaignSchema}
					onSubmit={(values, actions) => {
						setTimeout(async () => {
							await handleSaveCampaign(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => {
						// Só aprovado pode ser enviado — o resto a Meta recusa.
						const modelosAprovados = modelosWpp.filter((m) => m.aprovado);
						const modeloEscolhido = modelosAprovados.find(
							(m) => m.chave === values.metaTemplateChave,
						);

						return (
						<Form className="space-y-4">
							{/* Assistente em três passos. A tela inteira de uma vez tinha
							    dezoito campos e assustava quem nunca fez uma campanha: agora é
							    uma pergunta por vez, com um desenho do que vai acontecer. */}
							<div className="flex items-center gap-2">
								{PASSOS.map((p, indice) => (
									<button
										key={p.numero}
										type="button"
										onClick={() => setPasso(p.numero)}
										className="flex flex-1 items-center gap-2 text-left"
									>
										<span
											className={cn(
												"flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
												passo === p.numero
													? "bg-primary text-primary-foreground"
													: passo > p.numero
														? "bg-primary/15 text-primary"
														: "bg-muted text-muted-foreground",
											)}
										>
											{passo > p.numero ? <Check className="h-4 w-4" /> : p.numero}
										</span>
										<span className="min-w-0 flex-1">
											<span
												className={cn(
													"block whitespace-nowrap text-xs font-medium",
													passo === p.numero ? "text-foreground" : "text-muted-foreground",
												)}
											>
												{p.titulo}
											</span>
										</span>
										{indice < PASSOS.length - 1 && (
											<span className="hidden h-px flex-1 bg-border sm:block" />
										)}
									</button>
								))}
							</div>

							{/* O desenho: a mesma cena do passo, sem texto técnico. */}
							<div className="flex items-center gap-3 rounded-xl bg-muted/60 p-3">
								<DesenhoDoPasso passo={passo} />
								<div className="min-w-0">
									<p className="text-sm font-medium">{PASSOS[passo - 1].pergunta}</p>
									<p className="text-xs leading-relaxed text-muted-foreground">
										{PASSOS[passo - 1].explicacao}
									</p>
								</div>
							</div>

							{passo === 1 && (
								<div className="space-y-4">
										{!campaignId && (
											<div className="space-y-1.5">
												<Label>Quem vai receber</Label>
												<p className="text-xs text-muted-foreground">
													Escolha de um jeito só. Depois de criada, a lista de quem
													recebe não muda mais.
												</p>
												<Tabs value={targetTab} onValueChange={setTargetTab}>
													<TabsList className="grid w-full grid-cols-5">
														<TabsTrigger value="contacts">Escolher</TabsTrigger>
														<TabsTrigger value="list">Uma lista</TabsTrigger>
														<TabsTrigger value="tag">Por tag</TabsTrigger>
														<TabsTrigger value="queue">Por fila</TabsTrigger>
														<TabsTrigger value="group">Grupos</TabsTrigger>
													</TabsList>
													<TabsContent value="list" className="pt-2">
														<MultiSelect
															options={listas.map((lista) => ({
																value: String(lista.id),
																label: lista.items?.length
																	? `${lista.name} (${lista.items.length})`
																	: lista.name,
															}))}
															value={selectedListIds}
															onChange={setSelectedListIds}
															placeholder="Selecione uma ou mais listas"
														/>
														<p className="pt-1.5 text-xs text-muted-foreground">
															Quem ainda não é contato do sistema é cadastrado ao criar
															a campanha.
														</p>
													</TabsContent>
													<TabsContent value="contacts" className="space-y-2 pt-2">
														<Input
															placeholder="Buscar contatos..."
															value={contactSearch}
															onChange={(e) => setContactSearch(e.target.value)}
														/>
														<MultiSelect
															options={mergedContactOptions}
															value={selectedContacts.map((c) => String(c.id))}
															onChange={handleContactsChange}
															placeholder="Selecione os contatos"
														/>
													</TabsContent>
													<TabsContent value="tag" className="pt-2">
														<p className="pb-1.5 text-xs text-muted-foreground">
															Todo contato que tiver alguma dessas tags hoje entra na
															campanha — vale a etiqueta colada no contato (a carteira, na
															tela de Contatos) e a de um atendimento dele.
														</p>
														<MultiSelect
															options={tags.map((tag) => ({
																value: String(tag.id),
																label: tag.name,
																color: tag.color,
															}))}
															value={selectedTagIds}
															onChange={setSelectedTagIds}
															placeholder="Selecione uma ou mais tags"
														/>
													</TabsContent>
													<TabsContent value="queue" className="pt-2">
														<p className="pb-1.5 text-xs text-muted-foreground">
															Todo mundo que já foi atendido nessas filas entra na
															campanha — pode ser bastante gente.
														</p>
														<MultiSelect
															options={queues.map((fila) => ({
																value: String(fila.id),
																label: fila.name,
																color: fila.color,
															}))}
															value={selectedQueueIds}
															onChange={setSelectedQueueIds}
															placeholder="Selecione uma ou mais filas"
														/>
													</TabsContent>
												<TabsContent value="group" className="pt-2">
													<p className="pb-1.5 text-xs leading-relaxed text-muted-foreground">
														A mensagem aparece dentro do grupo, para todo mundo que está
														nele — não é conversa separada com cada pessoa.
													</p>
													{grupos.length === 0 ? (
														<p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
															Nenhum grupo por aqui ainda. Eles aparecem sozinhos quando chega
															mensagem de um grupo na conexão.
														</p>
													) : (
														<MultiSelect
															options={grupos.map((grupo) => ({
																value: String(grupo.id),
																label: grupo.name || grupo.number,
															}))}
															value={selectedGroupIds}
															onChange={setSelectedGroupIds}
															placeholder="Selecione um ou mais grupos"
														/>
													)}
												</TabsContent>
												</Tabs>
											</div>
										)}

										<div className="space-y-1.5">
											<Label>Sai por qual número</Label>
											<Select
												value={values.whatsappId ? String(values.whatsappId) : undefined}
												onValueChange={(v) => setFieldValue("whatsappId", v)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Selecione a conexão" />
												</SelectTrigger>
												<SelectContent>
													{whatsApps.map((whatsapp) => (
														<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
															{whatsapp.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<p className="text-xs text-muted-foreground">
												É desse WhatsApp que os clientes vão receber — e é nele que eles
												vão responder.
											</p>
											{touched.whatsappId && errors.whatsappId && (
												<p className="text-xs text-destructive">{errors.whatsappId}</p>
											)}
										</div>

								</div>
							)}

							{passo === 2 && (
								<div className="space-y-4">
										<div className="space-y-1.5">
											<div className="flex items-center justify-between gap-2">
												<Label htmlFor="body">O que cada pessoa vai receber</Label>
												{modelos.length > 0 && (
													<Select
														value=""
														onValueChange={(v) => {
															const modelo = modelos.find((m) => String(m.id) === v);
															// Preenche o campo em vez de guardar o vínculo: quem
															// monta a campanha quase sempre ajusta o texto, e um
															// modelo editado depois não deve mudar o que já saiu.
															if (modelo) setFieldValue("body", modelo.message || "");
														}}
													>
														<SelectTrigger className="h-8 w-52 text-xs">
															<SelectValue placeholder="Usar uma resposta pronta" />
														</SelectTrigger>
														<SelectContent>
															{modelos.map((modelo) => (
																<SelectItem key={modelo.id} value={String(modelo.id)}>
																	{modelo.shortcut}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												)}
											</div>
											<Field
												as={Textarea}
												id="body"
												name="body"
												rows={4}
												placeholder="Olá {{name}}, tudo bem?"
											/>
											<p className="text-xs text-muted-foreground">
												{
													"Todo mundo recebe o mesmo texto. Escreva {{name}} onde quiser o nome da pessoa — “Olá {{name}}” chega como “Olá Maria”."
												}
											</p>
											{touched.body && errors.body && (
												<p className="text-xs text-destructive">{errors.body}</p>
											)}
										</div>

										{/* Variações: a mesma campanha com dois ou três textos. Sorteando um
										    por pessoa, mil mensagens deixam de ser idênticas — é assim que o
										    WhatsApp identifica disparo em massa. Mandando todas, a mensagem
										    sai dividida em partes, uma embaixo da outra. */}
										<div className="space-y-2 rounded-xl border p-3">
											<div className="flex items-center justify-between gap-2">
												<Label className="text-sm">Outras versões do texto</Label>
												<Button
													type="button"
													variant="secondary"
													size="sm"
													onClick={() =>
														setFieldValue("bodyVariations", [
															...(values.bodyVariations || []),
															"",
														])
													}
												>
													<Plus className="h-4 w-4" />
													Adicionar
												</Button>
											</div>
											{(values.bodyVariations || []).length === 0 ? (
												<p className="text-xs leading-relaxed text-muted-foreground">
													Nenhuma. Todo mundo recebe exatamente o texto acima.
												</p>
											) : (
												<>
													{values.bodyVariations.map((texto, indice) => (
														// eslint-disable-next-line react/no-array-index-key
														<div key={indice} className="flex items-start gap-2">
															<span className="pt-2 text-xs text-muted-foreground">
																{indice + 2}ª
															</span>
															<Textarea
																rows={2}
																value={texto}
																placeholder="Outra forma de dizer a mesma coisa"
																onChange={(e) =>
																	setFieldValue(
																		"bodyVariations",
																		values.bodyVariations.map((t, i) =>
																			i === indice ? e.target.value : t,
																		),
																	)
																}
															/>
															<Button
																type="button"
																variant="ghost"
																size="icon"
																aria-label={`Apagar a ${indice + 2}ª versão`}
																onClick={() =>
																	setFieldValue(
																		"bodyVariations",
																		values.bodyVariations.filter((_, i) => i !== indice),
																	)
																}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													))}
													<div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
														{[
															{ chave: "random", rotulo: "Sortear uma" },
															{ chave: "all", rotulo: "Mandar todas" },
														].map((modo) => (
															<button
																key={modo.chave}
																type="button"
																onClick={() => setFieldValue("messageMode", modo.chave)}
																className={cn(
																	"rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
																	values.messageMode === modo.chave
																		? "bg-card text-foreground shadow-sm"
																		: "text-muted-foreground hover:text-foreground",
																)}
															>
																{modo.rotulo}
															</button>
														))}
													</div>
													<p className="text-xs leading-relaxed text-muted-foreground">
														{values.messageMode === "all"
															? "Cada pessoa recebe todas as versões, uma embaixo da outra — a mensagem dividida em partes."
															: "Cada pessoa recebe uma versão sorteada. Mensagens diferentes chamam menos atenção do WhatsApp que mil iguais."}
													</p>
												</>
											)}
										</div>

										{/* Modelo aprovado do WhatsApp oficial.
										    No WhatsApp oficial, mensagem para quem não falou nas
										    últimas 24 h só sai como modelo aprovado — e campanha é
										    justamente falar com quem não escreveu. Sem isto ela era
										    recusada contato a contato pelo erro 131047 da Meta. */}
										<div className="space-y-1.5 rounded-xl border p-3">
											<div className="flex flex-wrap items-center justify-between gap-2">
												<Label>Modelo aprovado (WhatsApp oficial)</Label>
												{values.metaTemplateChave && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => {
															setFieldValue("metaTemplateChave", "");
															setFieldValue("templateVariables", []);
															setVariaveisAnteriores(null);
														}}
													>
														Usar texto livre
													</Button>
												)}
											</div>

											{carregandoModelos ? (
												<p className="text-xs text-muted-foreground">
													Procurando modelos…
												</p>
											) : modelosAprovados.length === 0 ? (
												<p className="text-xs text-muted-foreground">
													Nenhum modelo aprovado. Eles vêm do WhatsApp oficial — pela
													Cloud API da Meta ou pelo WhatsApp conectado na Zernio. Numa
													conexão por celular não existe modelo: a campanha sai como
													texto livre.
												</p>
											) : (
												<>
													<Select
														value={values.metaTemplateChave || ""}
														onValueChange={async (chave) => {
															setFieldValue("metaTemplateChave", chave);

															const modelo = modelosAprovados.find(
																(m) => m.chave === chave,
															);

															const quantas = contarVariaveis(modelo?.components);

															setFieldValue(
																"templateVariables",
																Array(quantas).fill(""),
															);

															// O que foi preenchido da última vez neste mesmo
															// modelo. Quem dispara a mesma campanha toda semana
															// reescrevia os mesmos valores a cada vez.
															// É sugestão, não trava: os campos continuam
															// editáveis e o aviso diz de onde veio.
															setVariaveisAnteriores(null);

															if (!quantas) return;

															try {
																const { data } = await api.get(
																	"/campaigns/ultimas-variaveis",
																	{ params: { modelo: chave } },
																);

																if (!data?.variaveis?.length) return;

																setFieldValue(
																	"templateVariables",
																	Array.from(
																		{ length: quantas },
																		(_, i) => data.variaveis[i] || "",
																	),
																);
																setVariaveisAnteriores(data);
															} catch {
																// Sem sugestão a tela segue igual — os campos
																// ficam vazios, como antes.
															}
														}}
													>
														<SelectTrigger>
															<SelectValue placeholder="Sem modelo — enviar texto livre" />
														</SelectTrigger>
														<SelectContent>
															{modelosAprovados.map((modelo) => (
																<SelectItem key={modelo.chave} value={modelo.chave}>
																	{modelo.nome}
																	{modelo.idioma ? ` · ${modelo.idioma}` : ""}
																</SelectItem>
															))}
														</SelectContent>
													</Select>

													{variaveisAnteriores && (
													<p className="mt-1.5 rounded-md bg-muted/60 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
														Preenchido com o que foi usado em{" "}
														<b>{variaveisAnteriores.campanha || "uma campanha anterior"}</b>
														. Altere o que precisar.
													</p>
												)}

												{(values.templateVariables || []).map((valor, indice) => (
														// eslint-disable-next-line react/no-array-index-key
														<div key={indice} className="mt-1.5 space-y-1">
															<Label className="text-xs text-muted-foreground">
																{`{{${indice + 1}}}`}
															</Label>
															<Input
																value={valor}
																onChange={(e) =>
																	setFieldValue(
																		"templateVariables",
																		values.templateVariables.map((v, i) =>
																			i === indice ? e.target.value : v,
																		),
																	)
																}
															/>
														</div>
													))}

													{modeloEscolhido && (
														<div className="mt-2">
															<Label className="text-xs">O cliente vai ver</Label>
															<div className="mt-1.5">
																<TemplateMessage
																	dados={previaDoModelo(
																		modeloEscolhido,
																		values.templateVariables || [],
																	)}
																/>
															</div>
														</div>
													)}
												</>
											)}
										</div>

										{/* Guardado atrás de um resumo: quem não precisa mexer,
										    entende o estado sem abrir. */}
										<div className="rounded-xl border">
											<button
												type="button"
												onClick={() => setAvancadoAberto((v) => !v)}
												className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50"
											>
												<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
													<SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
												</span>
												<span className="min-w-0 flex-1">
													<span className="block text-sm font-medium">
														Ritmo de envio e anexo
													</span>
													<span className="block text-xs leading-relaxed text-muted-foreground">
														Uma mensagem a cada {values.intervalSeconds || 20}{" "}
														segundo(s)
														{values.mediaUrl ? " · com um link de anexo" : " · sem anexo"}
														{anexos.length > 0 ? ` · ${anexos.length} arquivo(s)` : ""}
													</span>
												</span>
												<ChevronDown
													className={cn(
														"h-4 w-4 shrink-0 text-muted-foreground transition-transform",
														avancadoAberto && "rotate-180"
													)}
												/>
											</button>

											{avancadoAberto && (
												<div className="space-y-4 border-t p-3">
													<div className="space-y-1.5">
														<Label htmlFor="intervalSeconds">
															Esperar quantos segundos entre uma mensagem e outra
														</Label>
														<Field
															as={Input}
															id="intervalSeconds"
															name="intervalSeconds"
															type="number"
															min={1}
														/>
														<p className="text-xs text-muted-foreground">
															Com 20 segundos, 100 contatos levam mais ou menos 33
															minutos. Deixe assim se estiver na dúvida.
														</p>
														{Number(values.intervalSeconds) > 0 &&
															Number(values.intervalSeconds) < 10 && (
																<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
																	<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
																	Menos de 10 segundos dispara muita mensagem em pouco
																	tempo. O WhatsApp pode entender como spam e bloquear
																	o seu número.
																</p>
															)}
														{touched.intervalSeconds && errors.intervalSeconds && (
															<p className="text-xs text-destructive">
																{errors.intervalSeconds}
															</p>
														)}
													</div>

													<div className="space-y-1.5">
														<Label htmlFor="intervalSecondsMax">
															Até quantos segundos (opcional)
														</Label>
														<Field
															as={Input}
															id="intervalSecondsMax"
															name="intervalSecondsMax"
															type="number"
															min={1}
															placeholder="Deixe em branco para ritmo fixo"
														/>
														<p className="text-xs text-muted-foreground">
															Preenchido, cada envio sorteia um tempo entre os dois.
															Ritmo cravado é o que mais parece robô — e é o que
															chama atenção do WhatsApp.
														</p>
													</div>

													<div className="space-y-1.5">
														<Label>Anexos (opcional)</Label>
														<div className="flex items-center gap-2">
															<Button
																type="button"
																variant="secondary"
																disabled={enviandoAnexo || anexos.length >= 10}
																onClick={() => anexoRef.current?.click()}
															>
																{enviandoAnexo ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Upload className="h-4 w-4" />
																)}
																Adicionar arquivo
															</Button>
															<span className="text-xs text-muted-foreground">
																{anexos.length}/10
															</span>
														</div>
														<input
															ref={anexoRef}
															type="file"
															className="hidden"
															onChange={(e) => enviarAnexo(e.target.files?.[0])}
														/>
														{anexos.length > 0 && (
															<div className="space-y-1 pt-1">
																{anexos.map((anexo, indice) => (
																	<div
																		key={anexo.mediaName}
																		className="space-y-1.5 rounded-md border px-2 py-1.5"
																	>
																		<div className="flex items-center gap-2">
																		<span className="flex-1 truncate text-xs">
																			{anexo.nomeOriginal || anexo.mediaName}
																		</span>
																		<button
																			type="button"
																			aria-label={`Remover ${
																				anexo.nomeOriginal || anexo.mediaName
																			}`}
																			onClick={() =>
																				setAnexos(
																					anexos.filter((_, i) => i !== indice)
																				)
																			}
																			className="text-muted-foreground hover:text-foreground"
																		>
																			<Trash2 className="h-3.5 w-3.5" />
																		</button>
																		</div>
																		{/* Legenda por arquivo: o backend já grava (`caption`), e sem
																		    ela a foto chega muda — quem recebe não sabe o que é. */}
																		<Input
																			className="h-8 text-xs"
																			placeholder="Legenda deste arquivo (opcional)"
																			value={anexo.caption || ""}
																			onChange={(e) =>
																				setAnexos(
																					anexos.map((a, i) =>
																						i === indice ? { ...a, caption: e.target.value } : a,
																					),
																				)
																			}
																		/>
																	</div>
																))}
															</div>
														)}
														<p className="text-xs text-muted-foreground">
															Saem depois do texto, um a um, na ordem da lista.
														</p>
													</div>

													{listasDeFrases.length > 0 && (
														<div className="space-y-1.5">
															<Label htmlFor="phraseListId">
																Variar o texto por uma lista de frases
															</Label>
															<Select
																value={values.phraseListId || "nenhuma"}
																onValueChange={(v) =>
																	setFieldValue(
																		"phraseListId",
																		v === "nenhuma" ? "" : v
																	)
																}
															>
																<SelectTrigger id="phraseListId">
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	<SelectItem value="nenhuma">
																		Não variar — usar sempre o texto acima
																	</SelectItem>
																	{listasDeFrases.map((lista) => (
																		<SelectItem
																			key={lista.id}
																			value={String(lista.id)}
																		>
																			{lista.name} ({lista.quantidade} variações)
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<p className="text-xs leading-relaxed text-muted-foreground">
																Cada contato recebe uma frase sorteada da lista, em
																vez do texto acima. Mil mensagens idênticas no mesmo
																minuto é o que o WhatsApp usa para identificar
																disparo em massa.
															</p>
														</div>
													)}

													<div className="space-y-1.5">
														<Label htmlFor="mediaUrl">
															Link de uma imagem ou arquivo (opcional)
														</Label>
														<Field
															as={Input}
															id="mediaUrl"
															name="mediaUrl"
															placeholder="https://..."
														/>
														<p className="text-xs text-muted-foreground">
															O link é colado no final da mensagem. Em branco, vai só o
															texto.
														</p>
													</div>

												</div>
											)}
										</div>


																		</div>
							)}

							{passo === 3 && (
								<div className="space-y-4">
									{/* Uma vez ou toda semana: a pergunta que decide se a campanha
									    repete. Fica antes da data porque muda o que vem depois. */}
									<div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
										{[
											{ chave: "once", rotulo: "Enviar uma vez" },
											{ chave: "recurring", rotulo: "Repetir sempre" },
										].map((opcao) => (
											<button
												key={opcao.chave}
												type="button"
												onClick={() => {
													setTipoDeEnvio(opcao.chave);
													if (opcao.chave === "once") {
														setFieldValue("recurrence", null);
													} else if (!values.recurrence) {
														setFieldValue("recurrence", { tipo: "daily" });
													}
												}}
												className={cn(
													"rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
													tipoDeEnvio === opcao.chave
														? "bg-card text-foreground shadow-sm"
														: "text-muted-foreground hover:text-foreground",
												)}
											>
												{opcao.rotulo}
											</button>
										))}
									</div>
										<div className="space-y-1.5">
											<Label htmlFor="scheduledFor">
												Data e hora (opcional)
											</Label>
											<Field
												as={Input}
												id="scheduledFor"
												name="scheduledFor"
												type="datetime-local"
											/>
											<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
												<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
												Com data marcada a campanha dispara sozinha na hora —
												ninguém precisa clicar em Iniciar. Em branco, ela fica
												em rascunho esperando o clique.
											</p>
										</div>

										{tipoDeEnvio === "recurring" && (
											<RecorrenciaFields
												recurrence={values.recurrence}
												onRecurrenceChange={(r) => setFieldValue("recurrence", r)}
												sendIfMissed={values.sendIfMissed}
												onSendIfMissedChange={(v) =>
													setFieldValue("sendIfMissed", v)
												}
												rotuloRecuperacao="Disparar mesmo se o horário já tiver passado"
											/>
										)}
<div className="space-y-1.5">
											<Label htmlFor="name">Nome da campanha</Label>
											<Field
												as={Input}
												id="name"
												name="name"
												autoFocus
												placeholder="Promoção de julho"
											/>
											<p className="text-xs text-muted-foreground">
												Só para você achar depois. O cliente não vê esse nome.
											</p>
											{touched.name && errors.name && (
												<p className="text-xs text-destructive">{errors.name}</p>
											)}
										</div>

										{/* Depois do envio: o trabalho que a equipe fazia na mão,
											    contato a contato, quando a campanha terminava. Fechado por
											    padrão — quem não usa nem precisa saber que existe. */}
											<div className="rounded-xl border">
												<button
													type="button"
													onClick={() => setAcoesAberto((v) => !v)}
													className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50"
												>
													<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
														<Zap className="h-4 w-4 text-muted-foreground" />
													</span>
													<span className="min-w-0 flex-1">
														<span className="block text-sm font-medium">
															O que acontece depois do envio
														</span>
														<span className="block text-xs leading-relaxed text-muted-foreground">
															{contarAcoes(values.postActions) +
																contarAcoes(values.replyActions) ===
															0
																? "Nada — a campanha termina quando a mensagem sai"
																: `${contarAcoes(values.postActions)} ao enviar · ${contarAcoes(
																		values.replyActions,
																	)} quando responderem`}
														</span>
													</span>
													<ChevronDown
														className={cn(
															"h-4 w-4 shrink-0 text-muted-foreground transition-transform",
															acoesAberto && "rotate-180",
														)}
													/>
												</button>

												{acoesAberto && (
													<div className="space-y-5 border-t p-3">
														<div className="space-y-3">
															<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
																Assim que a mensagem sair
															</p>
															<AcoesDaCampanhaFields
																acoes={values.postActions}
																onChange={(a) => setFieldValue("postActions", a)}
																tags={tags}
																queues={queues}
																raias={raias}
														atendentes={atendentes}
																momento="envio"
															/>
														</div>

														<div className="space-y-3 border-t pt-4">
															<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
																Quando a pessoa responder
															</p>
															<AcoesDaCampanhaFields
																acoes={values.replyActions}
																onChange={(a) => setFieldValue("replyActions", a)}
																tags={tags}
																queues={queues}
																raias={raias}
														atendentes={atendentes}
																momento="resposta"
															/>
															<p className="text-xs leading-relaxed text-muted-foreground">
																Vale para a primeira resposta em até 7 dias depois do
																disparo. Só em conversa individual — mensagem de grupo
																não conta.
															</p>
														</div>
													</div>
												)}
											</div>

																			</div>
							)}

							{/* A frase final: o que vai acontecer. Só no último passo — antes
							    dele a campanha ainda está sendo montada. */}
							{passo === 3 && (
								<div className="rounded-md bg-muted p-2.5 text-xs leading-relaxed">
								<p className="flex gap-1.5">
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
									<span>
										{campaignId ? (
											<>
												Salvar só muda o texto e os ajustes. Quem recebe continua
												sendo a mesma lista, e nada é enviado agora.
											</>
										) : (
											<>
												Vai atingir{" "}
												<span className="font-medium">{resumoDestinatarios}</span>
												, uma mensagem a cada {values.intervalSeconds || 20}{" "}
												segundo(s)
												{whatsApps.find(
													(w) => String(w.id) === String(values.whatsappId)
												)
													? `, pelo WhatsApp ${
															whatsApps.find(
																(w) => String(w.id) === String(values.whatsappId)
															).name
													  }`
													: ""}
												.
											</>
										)}
									</span>
								</p>
								{!campaignId && (
									<p className="mt-1.5 text-muted-foreground">
										Ao criar, ninguém recebe nada ainda. O envio começa no botão
										de play da lista, e o sistema pede confirmação antes.
									</p>
								)}
								</div>
							)}

							<DialogFooter className="gap-2 sm:justify-between">
								<Button
									type="button"
									variant="ghost"
									onClick={() => (passo === 1 ? handleClose() : setPasso(passo - 1))}
									disabled={isSubmitting}
								>
									{passo === 1 ? (
										"Cancelar"
									) : (
										<>
											<ArrowLeft className="h-4 w-4" />
											Voltar
										</>
									)}
								</Button>
								{/* Continuar não trava: quem monta a campanha às vezes quer ver o
								    passo seguinte antes de decidir. O que falta é cobrado na hora
								    de criar, com a mensagem do campo. */}
								{passo < 3 ? (
									<Button type="button" onClick={() => setPasso(passo + 1)}>
										Continuar
										<ArrowRight className="h-4 w-4" />
									</Button>
								) : (
									<Button type="submit" disabled={isSubmitting}>
										{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
										{campaignId ? "Salvar alterações" : "Criar sem enviar ainda"}
									</Button>
								)}
							</DialogFooter>
						</Form>
						);
					}}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default CampaignModal;
